import { useCallback, useEffect, useState } from 'react';
import { OTP } from '../../collections/collections.domain';
import { toCollectionURL } from '../../collections/mappers';
import { LiteUser } from '@client/new/common/LiteUser.mapper';
import { URLForm } from './useURLBuilderState';

export type URLData = {
  website: string;
  campaignName: string;
  campaignSource: string;
  collectionId?: number;
};

export type SetCollectionFN = (
  collection: OTP | { id: number; type: 'subscription' } | undefined,
) => void;

export const useURLBuilder = (influencerCustomUrl: LiteUser['customURL']) => {
  const [url, setUrl] = useState('');
  const isUrlGenerated = !!url;

  const [urlData, _setUrlData] = useState<URLData>(() => ({
    website: '',
    campaignName: '',
    campaignSource: '',
    collectionId: undefined,
  }));

  const setUrlData = <K extends keyof URLData>(key: K, value: URLData[K]) => {
    _setUrlData((prev) => ({ ...prev, [key]: value }));
  };

  const setCollection: SetCollectionFN = (collection) => {
    setUrlData('collectionId', collection?.id);
    setUrlData(
      'website',
      collection
        ? toCollectionURL(
            collection.type === 'subscription'
              ? {
                  type: 'subscription',
                  collection: { customURL: influencerCustomUrl },
                }
              : {
                  type: 'otp',
                  collection,
                },
          )
        : '',
    );
  };

  const createUrl = useCallback(() => {
    const params = new URLSearchParams({
      utm_source: urlData.campaignSource,
      utm_campaign: urlData.campaignName,
    });
    let v = `${urlData.website}?${params.toString()}`;
    if (!v.startsWith('http')) v = `https://${v}`;
    setUrl(v);
  }, [urlData]);

  useEffect(() => {
    if (isUrlGenerated) createUrl();
  }, [createUrl, isUrlGenerated]);

  return {
    url,
    urlData,
    setUrl,
    setUrlData,
    setCollection,
    createUrl,
    setData: (v: URLForm & { otp: OTP | undefined }) => {
      setUrlData('campaignName', v.name);
      setUrlData('campaignSource', v.source);
      setCollection(
        v.product === 'otp' && v.otp ? v.otp : { type: 'subscription', id: -1 },
      );
      createUrl();
    },
  };
};
